import { Box, Text } from '@chakra-ui/react';
import { useCollabQuotePreviewQuery } from '@/hooks/queries/use-collab-quote-preview-query';
import {
  COLLAB_PLATFORM_FEE_FLOOR_MINOR_UNITS,
  type BriefCompensation,
  type CollabSettlementQuote,
} from '@fluenceur/types';
import { useTranslation } from 'react-i18next';
import { CollabSettlementQuoteLines } from '@/pages/collabs/suivi/detail/execution/settlement/CollabSettlementQuoteLines';
import { formatCents } from './format-money';

const KEY = 'collabs.briefTemplates.sections.compensation.estimate';

interface CollabCostEstimateProps {
  /** A preview priced by the server, in the shape the panel later freezes. */
  quote: CollabSettlementQuote | null | undefined;
  isLoading?: boolean;
}

/**
 * What this collab will cost the brand, shown while the brief is still being
 * written.
 *
 * The surprise this kills is a line discovered at payment. So the estimate is
 * rendered by the very component the payment panel uses — same lines, same
 * order, same labels, same rates — and it is priced by the server from the same
 * policy objects rather than recomputed here. A composer doing its own
 * arithmetic is how the two screens end up disagreeing, which is the defect
 * this exists to prevent.
 *
 * The minimum is stated beside the rate rather than only when it bites. A small
 * collaboration is charged the floor instead of the percentage, and a reader
 * shown "10%" beside an amount that is not ten percent of anything is looking
 * at a figure nothing on screen explains. Naming both makes every amount
 * readable without the server having to tell us which of the two applied.
 *
 * Absent or still loading, it renders nothing: the estimate is a courtesy, and
 * a panel that appears, empties and reappears moves the controls under the
 * brand's cursor mid-composition.
 */
export const CollabCostEstimate = ({ quote, isLoading = false }: CollabCostEstimateProps) => {
  const { t, i18n } = useTranslation();

  if (isLoading || !quote) return null;

  const floor = formatCents(COLLAB_PLATFORM_FEE_FLOOR_MINOR_UNITS, quote.currency, i18n.language);

  return (
    <Box borderWidth="1px" borderColor="cardBorder" bg="panelBg" rounded="lg" px="14px" py="10px">
      <Text
        fontFamily="heading"
        fontSize="10.5px"
        fontWeight="semibold"
        textTransform="uppercase"
        letterSpacing="0.06em"
        color="fg.muted"
        mb="2"
      >
        {t(`${KEY}.title`)}
      </Text>

      <CollabSettlementQuoteLines quote={quote} showCreatorPrincipal />

      {/* An exempt workspace pays no fee, so it has no floor to be told about;
          the shared lines already drop the fee block above. */}
      {quote.feeExempt !== true && (
        <Text fontSize="11px" color="inkSubtle" lineHeight="1.4" mt="2">
          {t(`${KEY}.minimum`, { amount: floor })}
        </Text>
      )}
    </Box>
  );
};

/**
 * The estimate wired to what the brief currently promises.
 *
 * Only a paid brief has anything to be charged for: a gifted product is handed
 * over by the brand itself, never carried by the rail, so there is no principal
 * to price and no fee to announce. Quoting one anyway would invent a cost the
 * brand will never be asked for.
 *
 * The rule lives here rather than in each composer so the editor and the wizard
 * cannot answer it differently for the same brief.
 */
export const CollabCompensationEstimate = ({
  compensation,
}: {
  compensation: BriefCompensation;
}) => {
  const paid = compensation.type === 'fixed_payment' ? compensation.fixedPayment : null;
  const { quote, isLoading } = useCollabQuotePreviewQuery(
    paid?.amountCents ?? null,
    paid?.currency ?? null
  );

  return <CollabCostEstimate quote={quote} isLoading={isLoading} />;
};
